/**
 * Recipe Browser Screen
 *
 * Browse all 497 personal recipes with ingredient-based search.
 * Mealime-inspired design with circular images and match indicators.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ScrollView,
  ActivityIndicator,
  Platform,
  RefreshControl,
  TouchableOpacity,
  KeyboardAvoidingView,
  Animated,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { selectCurrentLanguage } from '../../src/redux/languageSlice';
import { isOfferedTo } from '../../src/utils/recipeLanguage';
import { useReportHiddenRecipes } from '../../src/hooks/useReadableRecipeIndex';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing } from '../../src/styles/theme';
import { RootState, AppDispatch } from '../../src/redux/store';
import {
  setRecipeIndex,
  invalidateRecipeIndex,
  selectRecipeIndex,
  isRecipeIndexStale,
} from '../../src/redux/recipeIndexSlice';
import { markStartup } from '../../src/utils/startupPerf';
import { AddRecipeModal } from '../../src/components/recipes/AddRecipeModal';
import { consumePendingImport } from '../../src/utils/pendingImport';
import { hasRealRecipeImage } from '../../src/utils/recipeImageVariant';
import unifiedRecipeService from '../../src/services/unifiedRecipeService';
import { prefetchRecipe } from '../../src/services/recipePrefetch';
import { isFirebaseEnabled } from '../../src/services/firebaseConfig';
import personalRecipeIndexService, {
  PersonalRecipeIndexEntry,
  mapEntriesToPersonal,
} from '../../src/services/personalRecipeIndex';
import { HeaderAvatarButton } from '../../src/components/HeaderAvatarButton';
import { matchesMealType } from '../../src/constants/dishTypes';
import { useRecipeEnrichment } from '../../src/hooks/useRecipeEnrichment';
import { useContentWidth } from '../../src/hooks/useContentWidth';
import {
  computeRecipeGridLayout,
  CARD_GAP,
  LIST_HORIZONTAL_PADDING,
} from '../../src/utils/recipeGridLayout';
import { Recipe } from '../../src/types/recipe';
import {
} from '../../src/redux/userPreferencesSlice';
import { useCookPreferences } from '../../src/hooks/useCookPreferences';
import { useTranslation } from 'react-i18next';
import { Toast, ToastType } from '../../src/components/common/Toast';
import { AnnouncementModal } from '../../src/components/announcements/AnnouncementModal';
import { dismissAnnouncement, selectDismissedAnnouncementIds } from '../../src/redux/announcementsSlice';
import {
  getFirstUnseenAnnouncement,
  waitForFeatureFlags,
  trackAnnouncementSeen,
  trackAnnouncementDismissed,
  trackAnnouncementCTAClicked,
} from '../../src/services/analyticsService';
import type { AnnouncementPayload } from '../../src/types/announcement';
import { OmniBar } from '../../src/components/recipes/OmniBar';
import { RecipeShelf } from '../../src/components/recipes/RecipeShelf';
import RecipeShelfCard, { detectRecipeDiet } from '../../src/components/recipes/RecipeShelfCard';
import { FilterSheet, FilterState, DEFAULT_FILTERS } from '../../src/components/recipes/FilterSheet';
import { ActiveFilterChips, type ExtraChip } from '../../src/components/recipes/ActiveFilterChips';
import { AiToken } from '../../src/components/recipes/AiToken';
import { UnderstoodLine } from '../../src/components/recipes/UnderstoodLine';
import { InlineAnswer } from '../../src/components/recipes/InlineAnswer';
import { IdeaOfDay } from '../../src/components/recipes/IdeaOfDay';
import { IDEA_ANGLES } from '../../src/components/recipes/ideaAngles';
import {
  buildFanSuggestions,
  dailySeed,
  localDateYMD,
} from '../../src/services/fanSuggestionsService';
import { selectActiveInventory, selectActivePantryNames } from '../../src/redux/inventorySlice';
import { rankRecipesByPantryMatch, pantryUsesByRecipe } from '../../src/services/pantryShelfService';
import {
  groupSearchResults,
  ROW_LIMIT,
  SearchRowKey,
  SectionedEntry,
} from '../../src/services/searchResultSections';
import type { RecipeCardNote } from '../../src/components/recipes/RecipeShelfCard';
import { SearchShortcutChips } from '../../src/components/recipes/SearchShortcutChips';
import type { ShortcutChipId } from '../../src/components/recipes/SearchShortcutChips';
import { useRecipeSearch, liveSearch } from '../../src/hooks/useRecipeSearch';
import { isAdminUser } from '../../src/utils/isAdmin';
import { useRecipeReviewCount } from '../../src/hooks/useRecipeReviewCount';

/**
 * Windowing for the recipe lists, which run over the full library (1300+).
 *
 * React Native's defaults mount far more rows than fit on screen; on the card
 * grids that means a burst of image requests for cards nobody has scrolled to.
 *
 * `removeClippedSubviews` is deliberately absent: it is unreliable on RN Web and
 * blanks cells during fast scroll, on a screen that has already had one INP
 * regression chased through it.
 */
const LIST_WINDOWING = {
  initialNumToRender: 12,
  maxToRenderPerBatch: 12,
  windowSize: 5,
} as const;


// `mapEntriesToPersonal` moved to personalRecipeIndex.ts (#668) so its field
// list is covered by a test.

/**
 * What the collapsed header leaves behind while searching — the gap that keeps
 * the search bar off the top edge once the title is gone. Matches the 2pt
 * paddingTop the header row contributes today plus a little breathing room.
 */
const HEADER_COLLAPSED_TOP = PaprikaSpacing.sm;

const EMPTY_DIETS: string[] = [];
const EMPTY_PANTRY_USES = new Map<string, string[]>();
/** How many of the pantry rows a recipe uses are named under its card. */
const PANTRY_NOTE_NAMES = 2;

/** Matches the veg dot in ActiveFilterChips, so a preference reads as a diet chip. */
const PREFERENCE_DOT_COLOR = '#5d7c43';

/** `dietaryRestrictions` values as stored, mapped to their i18n label key. */
const DIET_LABEL_KEYS: Record<string, string> = {
  vegetarian: 'vegetarian',
  vegan: 'vegan',
  'gluten-free': 'glutenFree',
  'dairy-free': 'dairyFree',
};

const RecipesScreen: React.FC = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { slotDate, slotMealType, filter } = useLocalSearchParams<{ slotDate?: string; slotMealType?: string; filter?: string }>();
  // Content width, not window width: the desktop sidebar eats 181px, and sizing
  // the grid off the window clipped the last column off the right edge.
  const contentWidth = useContentWidth();

  // Get authenticated user
  const { user, isGuest } = useSelector((state: RootState) => state.auth);
  const language = useSelector(selectCurrentLanguage);

  // Persisted recipe index — used for instant Phase 1 render on screen open.
  // Refs ensure loadRecipes (captured in stale useCallback closures) always reads
  // the latest value without adding these to the useFocusEffect dependency array.
  const cachedEntries = useSelector(selectRecipeIndex);
  const staleFlag = useSelector((state: RootState) =>
    isRecipeIndexStale(state, user?.uid ?? null)
  );
  const cachedEntriesRef = useRef(cachedEntries);
  const staleFlagRef = useRef(staleFlag);
  cachedEntriesRef.current = cachedEntries;
  staleFlagRef.current = staleFlag;

  // Replay a guest's pending import once they have an account. `consume` clears
  // as it reads, so this fires once and never reopens the sheet on a later visit.
  useEffect(() => {
    if (isGuest || !user?.uid) return;
    let cancelled = false;
    void consumePendingImport().then((pendingUrl) => {
      if (cancelled || !pendingUrl) return;
      setResumedImportUrl(pendingUrl);
      setIsAddModalVisible(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isGuest, user?.uid]);



  // Get recipe metadata from Redux (stable reference to prevent infinite loop)
  const recipeMetadata = useSelector((state: RootState) => state.recipeMetadata?.metadata);
  const recipeMetadataEntries = useSelector((state: RootState) => state.recipeMetadata?.metadata ?? {});
  // Active rows only, memoized (#668): the raw items array changed identity on
  // every pantry action and carried eaten/discarded rows into the matching.
  const inventoryItems = useSelector(selectActiveInventory);
  const activePantryNames = useSelector(selectActivePantryNames);

  // Derive recently viewed recipes (memoized to prevent infinite loop)
  const recentlyViewed = useMemo(() => {
    if (!recipeMetadata) return [];
    return Object.values(recipeMetadata)
      .filter((m): m is typeof m & { lastViewed: string } => m.lastViewed !== undefined)
      .map(m => ({ id: m.recipeId, timestamp: m.lastViewed }));
  }, [recipeMetadata]);

  // Responsive columns and card width, derived from the content column.
  const { numColumns, cardWidth } = useMemo(
    () => computeRecipeGridLayout(contentWidth),
    [contentWidth]
  );

  // State
  const [recipes, setRecipes] = useState<PersonalRecipeIndexEntry[]>([]);
  // What the shelves and « L'idée du jour » may show: a cook who just declared
  // a lactose allergy must not meet a gratin in the first band of the screen.
  // The pool « L'idée du jour » and the shelves draw from: everything the cook
  // ruled out is gone before an angle ever sees it — allergies, excluded
  // ingredients and declared diets, from the same resolver the planner reads.
  const declaredDiets = useSelector(
    (state: RootState) => state.userPreferences?.dietaryRestrictions ?? EMPTY_DIETS,
  );
  // A diet declared once in Réglages is a default, not a lock: the chip below
  // lifts it for this session only, and never writes back to the preference.
  // Allergies and excluded ingredients are not liftable and stay in force.
  const [ignoreDeclaredDiets, setIgnoreDeclaredDiets] = useState(false);
  const prefs = useCookPreferences({ ignoreDeclaredDiets });
  // The reader's language gate (#737): the shared catalogue is loaded ungated
  // and gated here, on read, because `recipes` outlives a language switch. A
  // cook's own saved recipes are never hidden.
  useReportHiddenRecipes(recipes, language, 'library');
  const safeRecipes = useMemo(() => {
    const offered = recipes.filter(r => isOfferedTo(r, language));
    return prefs.hasHardRules ? offered.filter(r => !prefs.isExcluded(r)) : offered;
  }, [recipes, prefs, language]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedMealType, setSelectedMealType] = useState<string | null>(
    filter === 'recent' ? 'recent' :
    (filter === 'best-rated' || filter === 'top-online') ? 'top-online' :
    'recent'
  );
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  // A link a guest pasted before the sign-up wall sent them away, replayed once
  // they come back with an account. Without this the URL is lost across the auth
  // navigation and the user has to go find it again.
  const [resumedImportUrl, setResumedImportUrl] = useState<string | null>(null);


  // New shelf mode state
  const [mode, setMode] = useState<'home' | 'search'>('home');
  const [rawQuery, setRawQuery] = useState('');
  /** The last query actually submitted — distinguishes "typing" from "searched". */
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [shelfFilters, setShelfFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Searching collapses the title / + / avatar so the bar rises into the header
  // row, iOS Mail style. Its height is measured rather than hardcoded: font
  // metrics and a guest's sign-in pill both move it.
  const searching = mode === 'search';
  const headerAnim = useRef(new Animated.Value(1)).current;
  const [headerHeight, setHeaderHeight] = useState<number | null>(null);

  useEffect(() => {
    Animated.timing(headerAnim, {
      toValue: searching ? 0 : 1,
      duration: 180,
      // `height` is not a transform, so the native driver cannot carry it —
      // and LayoutAnimation is a no-op on react-native-web, which is where
      // this screen actually ships.
      useNativeDriver: false,
    }).start();
  }, [searching, headerAnim]);

  // NLU search hook
  // Search never surfaces an allergen either: the hook folds `safeRecipes`,
  // and the live substring pass reuses those documents.
  const search = useRecipeSearch(safeRecipes, activePantryNames, recipeMetadataEntries);

  // Real-time search: show results as user types, before pressing Enter.
  // Reuses the hook's folded documents rather than re-folding every keystroke.
  //
  // Suppressed once the text has been submitted: the bar now keeps its query,
  // and live substring results would otherwise shadow the richer token results
  // (including the filters the model inferred) until the user typed again.
  const isEditingNewQuery = rawQuery.trim().length > 0 && rawQuery !== submittedQuery;
  const currentLiveResults = useMemo(
    () => (isEditingNewQuery ? liveSearch(rawQuery, search.searchDocs) : null),
    [isEditingNewQuery, rawQuery, search.searchDocs],
  );
  // What the search FlatList actually displays: live results while typing, token results after Enter.
  //
  // An empty live result must not shadow the hook's results — `[] ?? x` is `[]`,
  // which hid the near-miss fallback entirely: the "no exact match" banner
  // rendered above an empty grid.
  const searchDisplayResults =
    currentLiveResults && currentLiveResults.length > 0 ? currentLiveResults : search.results;

  // #847: a search opens on the household's own recipes, in rows like the home
  // screen's (loved, theirs newest first, what the pantry can make), and the
  // rest follows as the grid. Pantry rows are matched against the library once
  // per visit to search, not on every keystroke.
  const pantryUses = useMemo(
    () => (mode === 'search' ? pantryUsesByRecipe(safeRecipes, inventoryItems) : EMPTY_PANTRY_USES),
    [mode, safeRecipes, inventoryItems],
  );
  const showingTokenResults = searchDisplayResults === search.results;
  // Near-misses are approximate answers, not the household's dinners, and
  // « Surprenez-moi » asks for no ranking at all: both keep the plain grid.
  const groupResults =
    !(showingTokenResults && search.hasNoMatch) &&
    !(showingTokenResults && search.tokens.some(token => token.id === 'sort_random'));
  const groupedResults = useMemo(
    () =>
      groupResults
        ? groupSearchResults(searchDisplayResults, {
            ratingOf: id => recipeMetadataEntries[id]?.rating,
            pantryUsesOf: id => pantryUses.get(id),
          })
        : null,
    [groupResults, searchDisplayResults, recipeMetadataEntries, pantryUses],
  );
  // « Tout voir » on a row: the grid shows that row's recipes until the search
  // changes or the cook goes back to every result.
  const [expandedRow, setExpandedRow] = useState<SearchRowKey | null>(null);
  useEffect(() => {
    setExpandedRow(null);
  }, [searchDisplayResults, mode]);
  const expandedEntries =
    expandedRow && groupedResults?.layout === 'rows'
      ? groupedResults.rows.find(row => row.key === expandedRow)?.entries ?? null
      : null;
  const searchListEntries: SectionedEntry[] = useMemo(() => {
    if (expandedEntries) return expandedEntries;
    if (groupedResults?.layout === 'rows') return groupedResults.rest;
    if (groupedResults?.layout === 'grid') return groupedResults.entries;
    return searchDisplayResults.map(recipe => ({ recipe, section: 'rest' as const }));
  }, [expandedEntries, groupedResults, searchDisplayResults]);

  const rowTitle = useCallback(
    (key: SearchRowKey): string => {
      switch (key) {
        case 'loved':
          return t('recipes.shelves.topRated');
        case 'yours':
          return t('recipes.search.rows.yours');
        case 'pantry':
          return t('recipes.shelves.pantry');
      }
    },
    [t],
  );

  // The line under a card saying why it sits where it does.
  const searchNoteFor = useCallback(
    (entry: SectionedEntry): RecipeCardNote | undefined => {
      if (entry.section === 'yours' && entry.recipe._importedAt) {
        const addedAt = new Date(entry.recipe._importedAt);
        if (Number.isNaN(addedAt.getTime())) return undefined;
        const sameYear = addedAt.getFullYear() === new Date().getFullYear();
        const date = new Intl.DateTimeFormat(language, {
          day: 'numeric',
          month: 'short',
          ...(sameYear ? {} : { year: 'numeric' }),
        }).format(addedAt);
        return { text: t('recipes.search.rows.addedOn', { date }) };
      }
      if (entry.section === 'pantry') {
        const uses = pantryUses.get(entry.recipe.id);
        if (!uses?.length) return undefined;
        return { text: uses.slice(0, PANTRY_NOTE_NAMES).join(', '), tone: 'pantry' };
      }
      return undefined;
    },
    [t, language, pantryUses],
  );

  // Recently viewed recipes for the shelf
  const recentlyViewedRecipes = useMemo(() => {
    return recentlyViewed
      .slice()
      .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
      .slice(0, 12)
      .map(({ id }) => recipes.find(r => r.id === id))
      .filter((r): r is PersonalRecipeIndexEntry => r !== undefined);
  }, [recentlyViewed, recipes]);

  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<ToastType>('error');

  // Announcement state
  const dismissedAnnouncementIds = useSelector(selectDismissedAnnouncementIds);
  // Ref keeps the inner PostHog callback current — posthog.onFeatureFlags fires on every flag
  // reload (~30s), so we can't rely on the useCallback closure which captures the value once.
  const dismissedIdsRef = useRef(dismissedAnnouncementIds);
  useEffect(() => {
    dismissedIdsRef.current = dismissedAnnouncementIds;
  }, [dismissedAnnouncementIds]);
  const [currentAnnouncement, setCurrentAnnouncement] = useState<AnnouncementPayload | null>(null);
  const [announcementVisible, setAnnouncementVisible] = useState(false);
  const announcementChecked = useRef(false);

  const showToast = useCallback((message: string, type: ToastType = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  }, []);

  const handleAnnouncementDismiss = useCallback(() => {
    setAnnouncementVisible(false);
    if (currentAnnouncement) {
      dispatch(dismissAnnouncement(currentAnnouncement.id));
      trackAnnouncementDismissed(currentAnnouncement.id);
    }
  }, [currentAnnouncement, dispatch]);

  const handleAnnouncementCTA = useCallback((route: string) => {
    setAnnouncementVisible(false);
    if (currentAnnouncement) {
      dispatch(dismissAnnouncement(currentAnnouncement.id));
      trackAnnouncementCTAClicked(currentAnnouncement.id, route);
    }
  }, [currentAnnouncement, dispatch]);

  const handleSelectMealType = useCallback((type: string | null) => {
    setSelectedMealType(type);
  }, []);




  const isAdmin = isAdminUser(user?.email);
  const pendingReviewCount = useRecipeReviewCount(isAdmin);

  // useRecipeEnrichment requires a callback, but it persists its results itself —
  // they reach this screen through the next recipe-index refresh, not from here.
  const handleRecipeEnriched = useCallback((recipeId: string, data: Partial<Recipe>) => {
    console.log(`📊 Recipe enriched: ${recipeId} - ${data.readyInMinutes}min`);
  }, []);

  // Convert PersonalRecipeIndexEntry to Recipe-like format for enrichment hook
  const recipesForEnrichment = useMemo(() => {
    return recipes.map(r => ({
      id: r.id,
      title: r.title,
      readyInMinutes: r.readyInMinutes || 0,
      sourceUrl: r.sourceUrl,
      sourceRating: r.sourceRating,
      lastScrapedAt: r.lastScrapedAt,
      // The retry cap reads this (#668); omitting it made the cap dead.
      enrichmentAttemptCount: r.enrichmentAttemptCount,
    })) as Recipe[];
  }, [recipes]);

  // Use enrichment hook to auto-enrich recipes with missing cook time
  useRecipeEnrichment(recipesForEnrichment, handleRecipeEnriched);


  // Reload recipes whenever the screen is focused (e.g. after deleting a recipe)
  useFocusEffect(
    useCallback(() => {
      loadRecipes();
    }, [user?.uid])
  );

  // Check for PostHog announcements once per session when the user lands on recipes
  useFocusEffect(
    useCallback(() => {
      if (announcementChecked.current) return;
      announcementChecked.current = true;

      waitForFeatureFlags(() => {
        const result = getFirstUnseenAnnouncement(dismissedIdsRef.current);
        if (result) {
          setCurrentAnnouncement(result.payload);
          setAnnouncementVisible(true);
          trackAnnouncementSeen(result.payload.id, result.flagKey);
        }
      });
    }, [])
  );

  // Fire abandonment signal when user navigates away with active tokens
  useFocusEffect(
    useCallback(() => {
      return () => {
        search.signalAbandoned();
      };
    }, [search.signalAbandoned])
  );





  const hasMyTopRatedRecipes = useMemo(
    () => recipes.some(r => (recipeMetadata?.[r.id]?.rating ?? 0) >= 1),
    [recipes, recipeMetadata]
  );

  // Shelf 1: Personal top-rated (rating >= 4, sorted desc)
  const topRatedRecipes = useMemo(() => {
    return safeRecipes
      .filter(r => (recipeMetadataEntries[r.id]?.rating ?? 0) >= 4)
      .sort((a, b) => (recipeMetadataEntries[b.id]?.rating ?? 0) - (recipeMetadataEntries[a.id]?.rating ?? 0))
      .slice(0, 12);
  }, [safeRecipes, recipeMetadataEntries]);

  // Shelf 2: Community top (sourceRating >= 4.5 AND sourceRatingCount >= 5, Bayesian sort)
  const communityTopRecipes = useMemo(() => {
    const C = 10;
    const globalMean = 4.5;
    return safeRecipes
      .filter(r => (r.sourceRating ?? 0) >= 4.5 && (r.sourceRatingCount ?? 0) >= 5)
      .sort((a, b) => {
        const scoreA = (C * globalMean + (a.sourceRatingCount ?? 0) * (a.sourceRating ?? 0)) / (C + (a.sourceRatingCount ?? 0));
        const scoreB = (C * globalMean + (b.sourceRatingCount ?? 0) * (b.sourceRating ?? 0)) / (C + (b.sourceRatingCount ?? 0));
        return scoreB - scoreA;
      })
      .slice(0, 12);
  }, [safeRecipes]);

  // Shelf 3: Pantry matches (active inventory items)
  const pantryRecipes = useMemo(
    () => rankRecipesByPantryMatch(safeRecipes, activePantryNames),
    [safeRecipes, activePantryNames]
  );

  // Shelf 0: Recently added (any recipe with _importedAt in last 30 days, newest first)
  const recentlyAddedRecipes = useMemo(() => {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return safeRecipes
      .filter(r => r._importedAt && r._importedAt >= thirtyDaysAgo)
      .sort((a, b) => (b._importedAt ?? '').localeCompare(a._importedAt ?? ''))
      .slice(0, 12);
  }, [safeRecipes]);

  // "L'idée du jour": four recipes, each answering a different need. Same angle
  // engine as the planner's replace-fan, but seeded from the local date + uid so
  // the row survives a reload and only changes tomorrow.
  //
  // `todayYMD` is resolved once per mount on purpose: deriving it inline would
  // make the memo recompute on every render, and this screen has already had one
  // INP regression from unstable deps.
  const todayYMD = useMemo(() => localDateYMD(), []);
  const ideaSuggestions = useMemo(
    () =>
      buildFanSuggestions<PersonalRecipeIndexEntry>({
        allRecipes: safeRecipes,
        inventory: inventoryItems,
        metadataById: recipeMetadataEntries,
        seed: dailySeed(todayYMD, user?.uid),
        angleKeys: IDEA_ANGLES,
        prefer: (r) => hasRealRecipeImage(r.image),
      }),
    [safeRecipes, inventoryItems, recipeMetadataEntries, todayYMD, user?.uid]
  );

  // Filter helper: apply active filters to a recipe list
  const applyFilters = useCallback((recipesToFilter: PersonalRecipeIndexEntry[], f: FilterState): PersonalRecipeIndexEntry[] => {
    let result = recipesToFilter;

    if (f.diets.length > 0) {
      result = result.filter(r => {
        const diet = detectRecipeDiet(r);
        return f.diets.includes(diet);
      });
    }

    if (f.maxMinutes) {
      result = result.filter(r => r.readyInMinutes > 0 && r.readyInMinutes <= f.maxMinutes!);
    }

    if (f.minRating) {
      const threshold = f.minRating;
      result = result.filter(r =>
        (r.sourceRating ?? 0) >= threshold ||
        (recipeMetadataEntries[r.id]?.rating ?? 0) >= threshold
      );
    }

    if (f.mealTypes.length > 0) {
      result = result.filter(r =>
        f.mealTypes.some(mt => matchesMealType(r.dishTypes, mt))
      );
    }

    return result;
  }, [recipeMetadataEntries]);


  // The declared diets, shown as removable chips — a filter the cook cannot see
  // is a filter they cannot undo, and this one was saved on another screen.
  const preferenceChips = useMemo<ExtraChip[]>(() => {
    if (ignoreDeclaredDiets) return [];
    return declaredDiets.map(diet => ({
      id: `pref-${diet}`,
      label: t('recipes.filters.preference.diet', { diet: t(`diet.${DIET_LABEL_KEYS[diet] ?? diet}`) }),
      dietDot: PREFERENCE_DOT_COLOR,
      onRemove: () => setIgnoreDeclaredDiets(true),
    }));
  }, [declaredDiets, ignoreDeclaredDiets, t]);

  // Computed: active filter helpers
  const hasActiveFilters = shelfFilters.diets.length > 0 || shelfFilters.maxMinutes !== null ||
                           shelfFilters.minRating !== null || shelfFilters.mealTypes.length > 0;

  // When a filter is active we scan the ENTIRE library (not the curated shelves,
  // which are each capped at 12) and show every match in a flat grid. The subtitle
  // count derives from the same set so the number always matches what's rendered.
  const filteredLibraryRecipes = useMemo(
    () => applyFilters(safeRecipes, shelfFilters),
    [safeRecipes, shelfFilters, applyFilters]
  );

  const libraryTotal = safeRecipes.length;
  const filteredTotal = filteredLibraryRecipes.length;

  const handleRemoveFilter = (update: Partial<FilterState>) => {
    setShelfFilters(prev => ({ ...prev, ...update }));
  };

  // Fetches fresh recipe data from Firestore and updates both Redux cache and local state.
  // Falls back to static files when Firebase is unavailable.
  const fetchAndCacheRecipes = useCallback(async (uid: string | null) => {
    if (isFirebaseEnabled()) {
      try {
        const freshEntries = await unifiedRecipeService.getAllRecipes(uid ?? undefined);
        if (freshEntries.length > 0) {
          console.log(`✅ Loaded ${freshEntries.length} recipes from Firestore`);
          dispatch(setRecipeIndex({ entries: freshEntries, userId: uid }));
          setRecipes(mapEntriesToPersonal(freshEntries));
          return;
        }
        console.warn(`⚠️ Firestore returned 0 recipes (authenticated: ${!!uid}), falling back to static`);
      } catch (error) {
        console.warn('Failed to load from Firestore, falling back to static files:', error);
      }
    }
    console.log('📁 Loading recipes from static files...');
    const staticRecipes = await personalRecipeIndexService.getAllRecipes(language);
    console.log(`✅ Loaded ${staticRecipes.length} recipes from static files`);
    setRecipes(staticRecipes);
  }, [dispatch, language]);

  const loadRecipes = async (forceRefresh = false) => {
    const entries = cachedEntriesRef.current;
    const stale = staleFlagRef.current;

    // Phase 1: render from persisted cache immediately — no network wait
    if (entries.length > 0 && !forceRefresh) {
      markStartup('recipeIndex: cache hit');
      setRecipes(mapEntriesToPersonal(entries));
      setIsLoading(false);

      // Phase 2: silent background refresh when cache is stale
      if (stale) {
        fetchAndCacheRecipes(user?.uid ?? null).catch(() => {});
      }
      return;
    }

    // No cache (first load) or explicit force refresh: show spinner
    try {
      setIsLoading(true);
      await fetchAndCacheRecipes(user?.uid ?? null);
    } catch (error) {
      console.error('Error loading recipes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // Invalidate both the service in-memory cache and the Redux persist cache
    // so the forced pull-to-refresh always hits Firestore.
    unifiedRecipeService.invalidateCache();
    dispatch(invalidateRecipeIndex());
    await loadRecipes(true);
    setIsRefreshing(false);
  }, [dispatch]);

  const handleRecipePress = (recipe: PersonalRecipeIndexEntry, options?: { edit?: boolean }) => {
    // Asked for now, while the screen mounts; the screen's own read joins it (#874).
    prefetchRecipe(recipe.id);
    // Pass recipe index data for instant display
    router.push({
      pathname: `/(app)/recipe-detail/${recipe.id}`,
      params: {
        recipeData: JSON.stringify({
          id: recipe.id,
          title: recipe.title,
          image: recipe.image,
          servings: recipe.servings,
          readyInMinutes: recipe.readyInMinutes,
          dishTypes: recipe.dishTypes,
          cuisines: recipe.cuisines,
          diets: recipe.diets,
          ingredients: recipe.ingredients,
          sourceUrl: recipe.sourceUrl,
        }),
        ...(slotDate && { slotDate }),
        ...(slotMealType && { slotMealType }),
        ...(options?.edit && { edit: 'true' }),
      },
    });
  };

  // Add recipe modal handlers
  const handleAddRecipe = () => {
    setIsAddModalVisible(true);
  };

  const handleRecipeAdded = useCallback((recipeId: string, options?: { openEdit?: boolean; draft?: boolean }) => {
    setIsAddModalVisible(false);
    // Refresh the list to include the new recipe
    loadRecipes();
    // Navigate to the new recipe detail with from=add so back returns here
    const editParam = `${options?.openEdit ? '&edit=true' : ''}${options?.draft ? '&draft=1' : ''}`;
    router.push(`/(app)/recipe-detail/${recipeId}?from=add${editParam}`);
  }, [router]);




  const renderEmpty = () => {
    if (isLoading) return null;

    // Empty collection (no recipes at all) - show onboarding
    if (recipes.length === 0) {
      return (
        <View style={styles.emptyState}>
          <Feather name="book-open" size={48} color="#9B9B9B" />
          <Text style={styles.emptyTitle}>{t('recipes.addFirstRecipe')}</Text>
          <Text style={styles.emptySubtitle}>
            {t('recipes.importDescription')}
          </Text>
          <TouchableOpacity style={styles.emptyAddButton} onPress={handleAddRecipe} testID="add-recipe-button">
            <Feather name="plus" size={18} color="#FFFFFF" />
            <Text style={styles.emptyAddButtonText}>{t('recipes.addRecipe')}</Text>
          </TouchableOpacity>
        </View>
      );
    }

    // No personal ratings yet for My Top Rated filter
    if (selectedMealType === 'my-top-rated' && !hasMyTopRatedRecipes) {
      return (
        <View style={styles.emptyState}>
          <Feather name="star" size={48} color="#9B9B9B" />
          <Text style={styles.emptyTitle}>{t('recipes.myTopRatedEmpty')}</Text>
          <TouchableOpacity
            style={styles.emptyAddButton}
            onPress={() => handleSelectMealType(null)}
          >
            <Text style={styles.emptyAddButtonText}>{t('recipes.myTopRatedExplore')}</Text>
          </TouchableOpacity>
        </View>
      );
    }

    // Search/filter returned no results
    return (
      <View style={styles.emptyState}>
        <Feather name="search" size={48} color="#9B9B9B" />
        <Text style={styles.emptyTitle}>{t('recipes.noRecipes')}</Text>
        <Text style={styles.emptySubtitle}>
          {t('recipes.trySearching')}
        </Text>
      </View>
    );
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={Paprika.accent} />
        <Text style={styles.loadingText}>{t('recipes.loadingRecipes')}</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* Header — collapses while searching (see headerAnim above) */}
      <Animated.View
        style={[
          styles.headerCollapse,
          headerHeight != null && {
            height: headerAnim.interpolate({
              inputRange: [0, 1],
              // Never all the way to 0: HEADER_COLLAPSED_TOP is what keeps the
              // search bar off the top edge once the title is gone. This screen
              // has no SafeAreaView and Tabs runs headerShown:false, so nothing
              // else supplies a top inset.
              outputRange: [HEADER_COLLAPSED_TOP, headerHeight],
            }),
            // Fade out over the first two thirds so the text is gone before the
            // row finishes closing, rather than being squashed on the way down.
            opacity: headerAnim.interpolate({ inputRange: [0, 0.65, 1], outputRange: [0, 0, 1] }),
          },
        ]}
        pointerEvents={searching ? 'none' : 'auto'}
      >
        <View
          style={styles.headerRow}
          onLayout={e => {
            const h = e.nativeEvent.layout.height;
            // Only react to a real change — onLayout fires on every re-render
            // and sub-pixel churn would loop setState forever.
            if (h > 0 && (headerHeight == null || Math.abs(h - headerHeight) > 1)) {
              setHeaderHeight(h);
            }
          }}
        >
          {/* Shrinkable so a long FR subtitle can't push the actions off-screen */}
          <View style={styles.headerTitles}>
            <Text style={styles.screenTitle}>{t('recipes.title') || 'Recettes'}</Text>
            <Text style={styles.screenSubtitle} numberOfLines={1}>
              {hasActiveFilters
                ? t('recipes.filteredCount', { filtered: filteredTotal, total: libraryTotal })
                : t('recipes.libraryCount', { count: libraryTotal })}
            </Text>
          </View>
          <View style={styles.headerActions}>
            {/* Same 32px circle as the avatar beside it: one button shape in the header. */}
            <TouchableOpacity
              onPress={handleAddRecipe}
              testID="add-recipe-button"
              accessibilityLabel={t('recipes.addRecipe')}
              style={styles.headerAddBtn}
            >
              <Feather name="plus" size={18} color={Paprika.accent} />
            </TouchableOpacity>
            <HeaderAvatarButton />
          </View>
        </View>
      </Animated.View>

      {/* OmniBar */}
      <View style={styles.omniBarRow}>
        <OmniBar
          value={rawQuery}
          onChangeText={setRawQuery}
          onFocus={() => setMode('search')}
          onSubmitEditing={() => {
            if (rawQuery.trim()) {
              search.submitQuery(rawQuery);
              // The query stays in the bar. Clearing it hid which search was
              // still filtering, and an empty bar invites a new query that
              // silently ANDs onto the last one.
              setSubmittedQuery(rawQuery);
            }
          }}
          onCancel={() => {
            search.signalAbandoned();
            setMode('home');
            setRawQuery('');
            setSubmittedQuery('');
            search.reset();
          }}
          focused={mode === 'search'}
        />
      </View>

      {/* Shortcut chips — always visible; Filters leads them on the home screen */}
      <SearchShortcutChips
        activeTokenIds={search.tokens.map(t => t.id)}
        onChipPress={(chipId: ShortcutChipId) => {
          setMode('search');
          search.addShortcutToken(chipId);
        }}
        leading={mode === 'home' ? (
          <TouchableOpacity
            style={styles.filtersChip}
            onPress={() => setFilterSheetOpen(true)}
            testID="recipes-filters-button"
            activeOpacity={0.7}
          >
            <Feather name="sliders" size={13} color="#fff" />
            <Text style={styles.filtersChipText}>{t('recipes.filters.button')}</Text>
            {hasActiveFilters && (
              <View style={styles.filtersBadge}>
                <Text style={styles.filtersBadgeText}>
                  {shelfFilters.diets.length +
                    (shelfFilters.maxMinutes ? 1 : 0) +
                    (shelfFilters.minRating ? 1 : 0) +
                    shelfFilters.mealTypes.length}
                </Text>
              </View>
            )}
          </TouchableOpacity>
        ) : undefined}
      />

      {/* Applied filters and admin tools (home mode only) */}
      {mode === 'home' && (hasActiveFilters || preferenceChips.length > 0 || isAdmin) && (
        <View style={styles.filtersRow}>
          {(hasActiveFilters || preferenceChips.length > 0) && (
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chipsScroll} contentContainerStyle={{ gap: 8 }}>
              <ActiveFilterChips
                filters={shelfFilters}
                onRemoveFilter={handleRemoveFilter}
                extraChips={preferenceChips}
              />
            </ScrollView>
          )}
          {/* One door to the Review screen: new imports, the library and the
              catalogue are reviewed in one list there. The count is the new
              imports still waiting, the only queue that grows on its own. */}
          {isAdmin && (
            <TouchableOpacity
              testID="recipes-review-entry"
              style={styles.filtersBtn}
              onPress={() => router.push('/(app)/import-review')}
            >
              <Feather name="inbox" size={15} color={Paprika.primary} />
              <Text style={[styles.filtersBtnText, { color: Paprika.primary }]}>
                {pendingReviewCount > 0
                  ? t('importReview.entryCount', { count: pendingReviewCount })
                  : t('importReview.entry')}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      )}

      {/*
        Search hides the filters row, but the preference is still narrowing the
        results — and a filter the cook cannot see is a filter they cannot undo.
      */}
      {mode === 'search' && preferenceChips.length > 0 && (
        <View style={styles.preferenceChipsRow}>
          <ActiveFilterChips
            filters={DEFAULT_FILTERS}
            onRemoveFilter={() => {}}
            extraChips={preferenceChips}
          />
        </View>
      )}

      {/* Content: filtered library grid vs shelves vs search */}
      {mode === 'home' && hasActiveFilters ? (
        // Active filters scan the FULL library and show every match in a grid,
        // rather than re-filtering the capped (12-item) discovery shelves.
        <FlatList
          key={String(numColumns)}
          data={filteredLibraryRecipes}
          keyExtractor={r => r.id}
          numColumns={numColumns}
          {...LIST_WINDOWING}
          columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
          renderItem={({ item }) => (
            <RecipeShelfCard
              recipe={item}
              personalRating={recipeMetadataEntries[item.id]?.rating}
              width={cardWidth}
              onPress={() => handleRecipePress(item)}
            />
          )}
          contentContainerStyle={styles.searchList}
          keyboardShouldPersistTaps="handled"
          ListEmptyComponent={renderEmpty}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
        />
      ) : mode === 'home' ? (
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
        >
          <IdeaOfDay
            suggestions={ideaSuggestions}
            ratingOf={id => recipeMetadataEntries[id]?.rating}
            onPressRecipe={handleRecipePress}
          />

          {recentlyAddedRecipes.length > 0 && (
            <RecipeShelf
              title={t('recipes.shelves.recentlyAdded')}
              icon={<Feather name="download" size={16} color={Paprika.primary} />}
            >
              {recentlyAddedRecipes.map(r => (
                <RecipeShelfCard
                  key={r.id}
                  recipe={r}
                  personalRating={recipeMetadataEntries[r.id]?.rating}
                  onPress={() => handleRecipePress(r)}
                />
              ))}
            </RecipeShelf>
          )}

          {recentlyViewedRecipes.length > 0 && (
            <RecipeShelf
              title={t('recipes.shelves.recentlyViewed')}
              icon={<Feather name="eye" size={16} color={Paprika.primary} />}
            >
              {recentlyViewedRecipes.map(r => (
                <RecipeShelfCard
                  key={r.id}
                  recipe={r}
                  personalRating={recipeMetadataEntries[r.id]?.rating}
                  onPress={() => handleRecipePress(r)}
                />
              ))}
            </RecipeShelf>
          )}

          {topRatedRecipes.length > 0 && (
            <RecipeShelf
              title={t('recipes.shelves.topRated') || 'Top Rated'}
              icon={<Feather name="heart" size={17} color={Paprika.primary} />}
              action={t('recipes.shelves.seeAll')}
            >
              {topRatedRecipes.map(r => (
                <RecipeShelfCard
                  key={r.id}
                  recipe={r}
                  personalRating={recipeMetadataEntries[r.id]?.rating}
                  onPress={() => handleRecipePress(r)}
                />
              ))}
            </RecipeShelf>
          )}

          {communityTopRecipes.length > 0 && (
            <RecipeShelf
              title={t('recipes.shelves.communityTop') || 'Community Favorites'}
              icon={<Feather name="star" size={16} color={Paprika.primary} />}
              action={t('recipes.shelves.seeAll')}
            >
              {communityTopRecipes.map(r => (
                <RecipeShelfCard
                  key={r.id}
                  recipe={r}
                  personalRating={recipeMetadataEntries[r.id]?.rating}
                  onPress={() => handleRecipePress(r)}
                />
              ))}
            </RecipeShelf>
          )}

          {pantryRecipes.length > 0 && (
            <RecipeShelf
              title={t('recipes.shelves.pantry') || 'Pantry Matches'}
              icon={<Feather name="box" size={16} color={Paprika.primary} />}
              action={t('recipes.shelves.pantryCount', { count: pantryRecipes.length })}
            >
              {pantryRecipes.map(r => (
                <RecipeShelfCard
                  key={r.id}
                  recipe={r}
                  personalRating={recipeMetadataEntries[r.id]?.rating}
                  onPress={() => handleRecipePress(r)}
                />
              ))}
            </RecipeShelf>
          )}
        </ScrollView>
      ) : mode === 'search' ? (
        <FlatList
          key={String(numColumns)}
          data={searchListEntries}
          keyExtractor={entry => entry.recipe.id}
          numColumns={numColumns}
          {...LIST_WINDOWING}
          columnWrapperStyle={numColumns > 1 ? styles.columnWrapper : undefined}
          renderItem={({ item }) => (
            <RecipeShelfCard
              recipe={item.recipe}
              personalRating={recipeMetadataEntries[item.recipe.id]?.rating}
              note={searchNoteFor(item)}
              testID={`search-result-${item.section}`}
              width={cardWidth}
              onPress={() => {
                search.signalResultClicked();
                handleRecipePress(item.recipe);
              }}
            />
          )}
          contentContainerStyle={styles.searchList}
          keyboardShouldPersistTaps="handled"
          /*
            Search mode used to paint an empty canvas in two states: focused
            with nothing typed, and typed-but-nothing-found before the query
            is submitted. Every block in the header below is conditional, and
            in both cases none of them render. Collapsing the header took away
            the last thing on screen, so the whole viewport went blank — worst
            on desktop, where that is ~800px of nothing.

            `hasNoMatch` only gets set once a search resolves, and it prints
            its own line in the header, so defer to it when it is set rather
            than saying the same thing twice.
          */
          ListEmptyComponent={
            search.hasNoMatch ? null : (
              <View style={styles.searchIdle}>
                <Feather name="search" size={26} color={Paprika.textMuted} />
                <Text style={styles.searchIdleText}>
                  {!rawQuery.trim() && search.tokens.length === 0
                    ? t('recipes.searchIdleHint')
                    : t('recipes.noResultsAtAll')}
                </Text>
              </View>
            )
          }
          ListHeaderComponent={
            <View>
              <InlineAnswer
                answer={search.inlineAnswer}
                isLoading={search.isFollowUpLoading}
              />
              {search.tokens.length > 0 && (
                <>
                  <View style={styles.understoodRow}>
                    {search.tokens.some(token => token.source === 'ai') ? (
                      <UnderstoodLine />
                    ) : (
                      <Text style={styles.activeFiltersEyebrow}>
                        {t('recipes.activeFilters')}
                      </Text>
                    )}
                  </View>
                  {/*
                    Wraps rather than scrolls. An applied filter pushed off the
                    edge is state the user cannot see and cannot remove.
                  */}
                  <View style={styles.tokenRow}>
                    {search.tokens.map(token => (
                      <AiToken
                        key={token.id}
                        label={
                          token.labelKey
                            ? t(token.labelKey, token.labelParams)
                            : token.label
                        }
                        kind={token.kind}
                        neg={token.neg}
                        source={token.source}
                        onRemove={() => search.removeToken(token.id)}
                      />
                    ))}
                  </View>
                </>
              )}
              {search.hasNoMatch && (
                <View style={styles.eyebrowRow}>
                  <Text style={styles.noResultsText}>
                    {searchDisplayResults.length > 0
                      ? t('recipes.nearMissBanner')
                      : t('recipes.noResultsAtAll')}
                  </Text>
                </View>
              )}
              {searchDisplayResults.length > 0 && (
                <View style={styles.eyebrowRow}>
                  <Text style={styles.resultsEyebrow}>
                    {t('recipes.resultsCount', { count: searchDisplayResults.length })}
                    {search.isExactSearch ? ` · ${t('recipes.exactSearchEyebrow')}` : ''}
                    {search.hasNoMatch ? ` · ${t('recipes.nearMissEyebrow')}` : ''}
                  </Text>
                  {!search.isExactSearch && !search.hasNoMatch && search.lastRawQuery.trim().length > 0 && (
                    <TouchableOpacity onPress={() => search.setExactSearch(search.lastRawQuery)}>
                      <Text style={styles.exactSearchLink}>
                        {t('recipes.exactSearchLink')}
                      </Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
              {expandedRow && expandedEntries ? (
                <View style={styles.searchRowsHeading}>
                  <TouchableOpacity
                    testID="search-rows-back"
                    onPress={() => setExpandedRow(null)}
                    style={styles.searchRowsBack}
                    accessibilityRole="button"
                  >
                    <Feather name="chevron-left" size={16} color={Paprika.primary} />
                    <Text style={styles.searchRowsBackText}>{t('recipes.search.rows.backToAll')}</Text>
                  </TouchableOpacity>
                  <Text style={styles.searchRestTitle}>
                    {rowTitle(expandedRow)}
                    <Text style={styles.searchRestCount}>{`  ${expandedEntries.length}`}</Text>
                  </Text>
                </View>
              ) : groupedResults?.layout === 'rows' ? (
                <View style={styles.searchRows}>
                  {groupedResults.rows.map(row => (
                    <View key={row.key} style={styles.searchRow} testID={`search-row-${row.key}`}>
                      <RecipeShelf
                        title={rowTitle(row.key)}
                        count={row.entries.length}
                        icon={
                          <Feather
                            name={row.key === 'loved' ? 'heart' : row.key === 'yours' ? 'bookmark' : 'box'}
                            size={16}
                            color={Paprika.primary}
                          />
                        }
                        action={row.entries.length > ROW_LIMIT ? t('recipes.shelves.seeAll') : undefined}
                        onActionPress={() => setExpandedRow(row.key)}
                      >
                        {row.entries.slice(0, ROW_LIMIT).map(entry => (
                          <RecipeShelfCard
                            key={entry.recipe.id}
                            recipe={entry.recipe}
                            personalRating={recipeMetadataEntries[entry.recipe.id]?.rating}
                            note={searchNoteFor(entry)}
                            testID={`search-row-card-${row.key}`}
                            onPress={() => {
                              search.signalResultClicked();
                              handleRecipePress(entry.recipe);
                            }}
                          />
                        ))}
                      </RecipeShelf>
                    </View>
                  ))}
                  {groupedResults.rest.length > 0 && (
                    <Text style={[styles.searchRestTitle, styles.searchRestTitleInset]} testID="search-rest-title">
                      {t('recipes.search.rows.rest')}
                      <Text style={styles.searchRestCount}>{`  ${groupedResults.rest.length}`}</Text>
                    </Text>
                  )}
                </View>
              ) : null}
            </View>
          }
        />
      ) : null}

      <FilterSheet
        visible={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        filters={shelfFilters}
        onApply={(newFilters) => {
          setShelfFilters(newFilters);
          setFilterSheetOpen(false);
        }}
        resultCount={filteredTotal}
        countForFilters={(f) => applyFilters(recipes, f).length}
      />

      {/* Add Recipe Modal */}
      <AddRecipeModal
        visible={isAddModalVisible}
        initialUrl={resumedImportUrl}
        onClose={() => {
          setIsAddModalVisible(false);
          setResumedImportUrl(null);
        }}
        onSuccess={handleRecipeAdded}
        userId={user?.uid || ''}
      />

      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onDismiss={() => setToastVisible(false)}
      />
      <AnnouncementModal
        visible={announcementVisible}
        announcement={currentAnnouncement}
        onDismiss={handleAnnouncementDismiss}
        onCTA={handleAnnouncementCTA}
      />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Paprika.background,
  },
  loadingText: {
    ...PaprikaTypography.bodyMedium,
    marginTop: PaprikaSpacing.md,
    color: Paprika.textSecondary,
  },
  listContent: {
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.lg,
    paddingTop: PaprikaSpacing.sm,
  },
  columnWrapper: {
    justifyContent: 'flex-start',
    gap: CARD_GAP,
    marginBottom: CARD_GAP,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.xxl,
    paddingHorizontal: PaprikaSpacing.lg,
  },
  emptyIcon: {
    fontSize: 48,
    marginBottom: PaprikaSpacing.lg,
  },
  emptyTitle: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.sm,
    textAlign: 'center',
  },
  emptySubtitle: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },
  emptyAddButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: PaprikaSpacing.lg,
    paddingVertical: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.xl,
    backgroundColor: Paprika.accent,
    borderRadius: 24,
    gap: 8,
  },
  emptyAddButtonText: {
    ...PaprikaTypography.labelMedium,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  // New shelf-based styles
  headerTitles: {
    flex: 1,
    minWidth: 0,
    marginRight: 12,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  headerCollapse: {
    // The animated height clips the title as it closes; without this the text
    // spills over the search bar for the length of the transition.
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingBottom: 12,
    paddingTop: 2,
  },
  screenTitle: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 31,
    color: Paprika.textPrimary,
    letterSpacing: -0.02 * 31,
  },
  screenSubtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12.5,
    color: Paprika.textMuted,
    marginTop: 4,
  },
  omniBarRow: {
    paddingHorizontal: 18,
    paddingBottom: 12,
  },
  filtersRow: {
    paddingHorizontal: 18,
    paddingBottom: 16,
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filtersBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 40,
    paddingHorizontal: 15,
    borderRadius: 12,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#ece0d4',
  },
  filtersBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    color: Paprika.textPrimary,
  },
  // Sized to the shortcut chips it sits in front of (36px pill).
  filtersChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 36,
    paddingHorizontal: 13,
    borderRadius: 18,
    backgroundColor: Paprika.textPrimary,
  },
  filtersChipText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: '#fff',
  },
  headerAddBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filtersBadge: {
    minWidth: 19,
    height: 19,
    paddingHorizontal: 5,
    borderRadius: 999,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filtersBadgeText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    color: '#fff',
  },
  preferenceChipsRow: {
    paddingHorizontal: LIST_HORIZONTAL_PADDING,
    paddingBottom: 8,
  },
  chipsScroll: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
    gap: 24,
  },
  searchList: {
    paddingHorizontal: LIST_HORIZONTAL_PADDING,
    paddingTop: 4,
    gap: 10,
    paddingBottom: 32,
  },
  // The rows bleed to the screen edge like the home shelves; the list's own
  // padding would otherwise inset them a second time.
  searchRows: {
    marginHorizontal: -LIST_HORIZONTAL_PADDING,
  },
  searchRow: {
    marginBottom: 24,
  },
  searchRestTitle: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 17,
    color: Paprika.textPrimary,
  },
  searchRestTitleInset: {
    paddingHorizontal: LIST_HORIZONTAL_PADDING,
    marginBottom: 12,
  },
  searchRestCount: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
  searchRowsHeading: {
    gap: 6,
    marginBottom: 12,
  },
  searchRowsBack: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    alignSelf: 'flex-start',
    paddingVertical: 4,
  },
  searchRowsBackText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13,
    color: Paprika.primary,
  },
  searchIdle: {
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingTop: 56,
    paddingHorizontal: PaprikaSpacing.lg,
  },
  searchIdleText: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textMuted,
    textAlign: 'center',
    maxWidth: 320,
  },
  resultsEyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.14 * 11,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
  },
  understoodRow: {
    paddingHorizontal: 18,
    marginTop: 4,
    marginBottom: 4,
  },
  tokenRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 18,
    paddingVertical: 8,
    gap: 8,
  },
  activeFiltersEyebrow: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 11,
    letterSpacing: 0.7,
    color: Paprika.textSecondary,
  },
  eyebrowRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 18,
    paddingTop: 4,
    paddingBottom: 10,
  },
  exactSearchLink: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.primary,
  },
  noResultsText: {
    fontFamily: 'Inter-Regular',
    fontSize: 13,
    color: Paprika.textMuted,
  },
});

export default RecipesScreen;
